import React from 'react';
import { observer } from 'mobx-react-lite';
import { runInAction, toJS } from 'mobx';
import { getRootStoreInstance } from 'store/rootStore';
import { Button, colors, ProposalCard } from '@insurancemenu/imenu-components-lib';
import DynamicFormModalDEPRECATED from 'components/common/modals/DynamicFormModal_DEPRECATED';
import { ModalType } from 'global/utils/ModalsHelper';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { useHistory, useParams } from 'react-router-dom';
import {
  ProductLineDelegateContext,
  useRFQProductLineDelegate,
} from 'store/pages/rfq/rfq-response/proposal-rfq/rfqProductLineContext';
import ProductLineDelegate from 'store/modules/proposal/delegates/productLineDelegate';
import {
  CartItemDelegateContext,
  useRFQCartItemDelegate,
} from 'store/pages/rfq/rfq-response/proposal-rfq/rfqCartItemContext';
import CartItemDelegate from 'store/modules/proposal/delegates/cartItemDelegate';
import ManageTagModal from 'components/common/proposal/ManageTagModal';
import { ProposalPageType, useRFQProposalPageType } from 'global/hooks/useRFQProposalPageType';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { createStyles } from './styles';
import AddCustomLinkModal from '../AddCustomLinkModal';
import RFQCartItemProductLineHeader from '../RFQCartItemProductLineHeader';
import useManageTagModalStore from './useManageTagModalStore';

export interface ProposalPlansProps {
  type: ProposalPageType;
}

const RFQManageTagModal = observer(() => {
  const store = useManageTagModalStore();
  return <ManageTagModal store={store} />;
});

const RemovePortfolioModal = observer(() => {
  const { portfolioName, closeRemovePortfolioModal, removePortfolio, modalIsVisible } = useRFQCartItemDelegate();

  const classes = createStyles();
  return (
    <ActionModal
      show={modalIsVisible}
      title="Remove Plan"
      subtitle={
        <span>
          You&apos;re about to remove
          <b className={classes.currentName}>{portfolioName || 'this'} </b> plan from proposal.
        </span>
      }
      actionButtonText="Remove"
      closeButtonText="Close"
      onClose={closeRemovePortfolioModal}
      onAction={removePortfolio}
      modalType={ModalType.DELETE}
    />
  );
});

const CartItem = observer(() => {
  const { periodId } = useParams<{ periodId: string }>();
  const history = useHistory();
  const pageType = useRFQProposalPageType();

  const productLineDelegate = useRFQProductLineDelegate();
  const cartItemDelegate = useRFQCartItemDelegate();

  const {
    portfolioConfigModalStore: { openConfigurePlanModal },
    proposalItemsStore: {
      cart: { id: cartId, lastUpdateBrowsingCriteria },
      cartItems,
      setBrowsingCriteria,
      onViewPlanDetails,
      getProductLineRateFormId,
      openRemoveRateModal,
      openRemoveFileModal,
    },
    rateRFQModalStore: { openRateRFQPlanModal },
    uploadFileModalStore: { openUploadModal },
  } = getRootStoreInstance();

  // Open Configure Portfolio modal (RELEASE type for Shelf portfolios)
  async function configurePortfolio(): Promise<void> {
    await openConfigurePlanModal(
      cartItemDelegate.wholePortfolio,
      cartId,
      lastUpdateBrowsingCriteria.effectiveDate,
      cartItems,
    );
  }

  function browsingChange(id: string, selectedBcIndex: number): void {
    setBrowsingCriteria(id, selectedBcIndex);
  }

  function viewPlanDetails(): void {
    onViewPlanDetails(cartItemDelegate.portfolioId, history.push);
  }

  function isRFQPlanConfigurable(): boolean {
    return pageType === 'CREATE_PORTFOLIO' ? cartItemDelegate.isPlanConfigurable : false;
  }

  const openManageProductTagModal = (_: string, productId: string, tag: string): void => {
    cartItemDelegate.openManageTagModal({ productId, tag, periodId });
  };

  /**
   * Represents a Proposal Card type, where the view varies depending on the type.
   * This differs from the page type. The card does not require the "RELEASE" or "CREATE_PORTFOLIO"
   * special views because the "GENERAL" view is used instead.
   */
  const rfqProposalType = pageType === 'RELEASE' || pageType === 'CREATE_PORTFOLIO' ? 'GENERAL' : pageType;

  const classes = createStyles();
  return (
    <div key={`proposal_card_${cartItemDelegate.portfolioId}`} className={classes.cartItem}>
      {/* Display an external loader exclusively for portfolios where triggers a cart update
       * (like add/remove bc version). For other scenarios, rely on the loader integrated
       * within the component. */}
      {cartItemDelegate.isPortfolioLoadingWithRates ? (
        <div className={classes.proposalFullyLoader}>
          <FormsSpinner display semiTransparentBg={false} />
        </div>
      ) : (
        <div className={classes.proposalCardWrapper}>
          {pageType === 'CREATE_PORTFOLIO' && (
            <div className={classes.removeButtonWrapper}>
              <Button
                type="outlined"
                color="custom"
                size="large"
                customColor={colors.red[600]}
                onClick={cartItemDelegate.openRemovePortfolioModal}
                disabled={productLineDelegate.portfolioToCartItems.length === 1}
              >
                Remove
              </Button>
            </div>
          )}
          <ProposalCard
            // Only product lines with RFQ browsing type should be shown with another view
            type={cartItemDelegate.overrideRatesEligible ? rfqProposalType : 'GENERAL'}
            productLineAttributes={productLineDelegate.productLineAttributes.map((dateObj) => toJS(dateObj)).slice()}
            marketingTags={cartItemDelegate.marketingTags.map((dateObj) => toJS(dateObj)).slice()}
            portfolio={toJS(cartItemDelegate.wholePortfolio)}
            cartItem={toJS(cartItemDelegate.wholeCartItem)}
            // isPlanConfigurable only affects the general card type.
            isPlanConfigurable={isRFQPlanConfigurable()}
            isProposalConfirmed={cartItemDelegate.isProposalConfirmed}
            areCustomLinksRemovable={pageType === 'FILE_UPLOAD'}
            userRoles={cartItemDelegate.userRoles.map((dateObj) => toJS(dateObj)).slice()}
            loading={cartItemDelegate.isPortfolioLoading}
            onConfigurePortfolio={configurePortfolio}
            onBrowsingChange={browsingChange}
            onViewPlanDetails={viewPlanDetails}
            onManageProductTag={openManageProductTagModal}
            // RATE type only
            onConfigureRateVariant={(_id, bcIndex: number, modalType: 'ADD' | 'UPDATE'): void => {
              // RFQ portfolio supports only one carrier per portfolio.
              const carrierId = cartItemDelegate.wholePortfolio.products[0].carrier.id;
              void openRateRFQPlanModal(
                getProductLineRateFormId(productLineDelegate.productLineId, carrierId),
                { portfolio: cartItemDelegate.wholePortfolio, cartItem: cartItemDelegate.wholeCartItem },
                lastUpdateBrowsingCriteria,
                bcIndex,
                modalType,
              );
            }}
            onRemoveRateVariant={
              // Show remove rate button only for RFQ products
              cartItemDelegate.overrideRatesEligible
                ? (_id, bcIndex: number): void => {
                    openRemoveRateModal(cartItemDelegate.wholeCartItem, bcIndex);
                  }
                : undefined
            }
            // FILE_UPLOAD (Collateral) type only
            onAddCustomLink={openUploadModal}
            onRemoveCustomLink={openRemoveFileModal}
          />
        </div>
      )}
    </div>
  );
});

const CartItemsCollection = observer(() => {
  const productLineDelegate = useRFQProductLineDelegate();
  const { rfqProposalPageStore } = getRootStoreInstance();
  const classes = createStyles();
  return (
    <div className={classes.cartItemsContent}>
      {productLineDelegate.portfolioToCartItems.map((item) => (
        <CartItemDelegateContext.Provider
          value={
            new CartItemDelegate({
              pageStore: rfqProposalPageStore,
              productLineDelegate,
              portfolioToCartItem: item,
            })
          }
          key={item.portfolio.id}
        >
          <CartItem />
          <RFQManageTagModal />
          <RemovePortfolioModal />
        </CartItemDelegateContext.Provider>
      ))}
    </div>
  );
});

const RFQProposalPlans = observer((): JSX.Element => {
  // Mobx store
  const {
    proposalItemsStore: {
      portfoliosByProductLine,
      isProposalEditable,
      cart: { id: cartId, lastUpdateBrowsingCriteria },
      cartItems,
      showRemoveRateModal,
      removedRateInfo,
      fullCart,
      showRemoveFileModal,
      removedFileInfo,
      setLoadingForPortfolio,
      getPortfolioById,
      updateCartItem,
      closeRemoveRateModal,
      removeProductRate,
      removeCustomLink,
      closeRemoveFileModal,
      updateCustomPortfolioAndCart,
      resetRemovedRateInfo,
    },
    portfolioConfigModalStore: {
      showConfigurePlanModal,
      dynamicForm,
      loading,
      form,
      portfolioId: configurePortfolioId,
      saveAnswers,
      closeConfigurePlanModal,
      openConfigurePlanModal,
    },
    rateRFQModalStore: {
      loading: rateProposalModalLoading,
      showRateRfqModal,
      dynamicForm: rateDynamicForm,
      form: rateForm,
      portfolio: rateProposalPortfolio,
      closeRateRFQModalModal,
      saveProposalRatesAnswers,
    },
    rfqProposalPageStore,
  } = getRootStoreInstance();

  const convertedPortfolioItemsByProductLines = portfoliosByProductLine.map((dateObj) => toJS(dateObj)).slice();
  const productLines = convertedPortfolioItemsByProductLines.map((item) => item.productLineId);

  const convertedRemovedRateInfo = toJS(removedRateInfo);
  const convertedRemovedFileInfo = toJS(removedFileInfo);

  // Open Configure Portfolio modal (RELEASE type for Shelf portfolios)
  async function configurePortfolio(portfolioId: string): Promise<void> {
    const portfolio = getPortfolioById(portfolioId);
    if (portfolio) {
      await openConfigurePlanModal(portfolio, cartId, lastUpdateBrowsingCriteria.effectiveDate, cartItems);
    }
  }

  // Configure Portfolio (RELEASE type for Shelf portfolios)
  const savePortfolioConfigModalChanges = async (): Promise<void> => {
    setLoadingForPortfolio(configurePortfolioId, true);
    closeConfigurePlanModal();
    const updatedCard = await saveAnswers().catch(async (): Promise<null> => {
      setLoadingForPortfolio(configurePortfolioId, false);
      await configurePortfolio(configurePortfolioId);
      return null;
    });
    await runInAction(async () => {
      if (updatedCard?.data?.addPortfolioConfigToCart) {
        await updateCartItem(updatedCard.data?.addPortfolioConfigToCart, configurePortfolioId);
      }
    });
  };

  // Add proposal rates (RATE type)
  const addProposalRate = async (): Promise<void> => {
    if (!rateProposalPortfolio) return;
    setLoadingForPortfolio(rateProposalPortfolio.id, true);
    closeRateRFQModalModal();
    const updatedCard = await saveProposalRatesAnswers(cartId);
    if (updatedCard) {
      await updateCustomPortfolioAndCart(updatedCard, rateProposalPortfolio.id);
    }
  };

  // Remove proposal rates (all types)
  const removeProposalRate = async (): Promise<void> => {
    if (!convertedRemovedRateInfo.portfolioId) return;
    setLoadingForPortfolio(convertedRemovedRateInfo.portfolioId, true);
    closeRemoveRateModal();
    const updatedCard = await removeProductRate(convertedRemovedRateInfo);
    if (updatedCard) {
      await updateCustomPortfolioAndCart(updatedCard, convertedRemovedRateInfo.portfolioId);
      resetRemovedRateInfo();
    }
  };

  // Update FILE_UPLOAD type proposal
  const updateFileUploadProposal = async (portfolioId: string): Promise<void> => {
    setLoadingForPortfolio(portfolioId, true);
    await updateCartItem(toJS(fullCart), portfolioId, 'no-cache');
    setLoadingForPortfolio(portfolioId, false);
  };

  // Remove custom Link (FILE_UPLOAD type, can be changed via 'areCustomLinksRemovable' prop)
  const removeProductCustomLink = async (): Promise<void> => {
    closeRemoveFileModal();
    await removeCustomLink(
      cartId,
      convertedRemovedFileInfo.portfolioId,
      convertedRemovedFileInfo.productId,
      convertedRemovedFileInfo.linkName,
    );

    // Update portfolio to show updated links list
    void updateFileUploadProposal(convertedRemovedFileInfo.portfolioId);
  };

  const classes = createStyles();
  return (
    <>
      <div className={classes.proposalsList}>
        {productLines &&
          productLines.map((productLineId) => (
            <section className={classes.productLineSection} key={`proposal_card_section_${productLineId}`}>
              <ProductLineDelegateContext.Provider value={new ProductLineDelegate(rfqProposalPageStore, productLineId)}>
                <RFQCartItemProductLineHeader productLineId={productLineId} />
                <CartItemsCollection />
              </ProductLineDelegateContext.Provider>
            </section>
          ))}
      </div>
      {/* Configure Modal for Shelf browsing type */}
      <DynamicFormModalDEPRECATED
        show={showConfigurePlanModal}
        onClose={closeConfigurePlanModal}
        onAction={savePortfolioConfigModalChanges}
        dynamicForm={dynamicForm}
        form={form}
        loading={loading}
        isEditable={isProposalEditable}
        formStyles={classes.bcForm}
      />
      {/* Rate RFQ Modal for RFQ browsing type */}
      <DynamicFormModalDEPRECATED
        show={showRateRfqModal}
        onClose={closeRateRFQModalModal}
        onAction={addProposalRate}
        dynamicForm={rateDynamicForm}
        form={rateForm}
        loading={rateProposalModalLoading}
        formStyles={classes.bcForm}
      />
      <ActionModal
        show={showRemoveRateModal}
        title="Delete rated version"
        subtitle={
          <span>
            You&apos;re about to delete
            <b className={classes.currentName}>{convertedRemovedRateInfo.shortName || 'the current'} version.</b> Easily
            add a new one using the Add/Rate Configuration button.
          </span>
        }
        actionButtonText="Delete"
        closeButtonText="Close"
        onClose={closeRemoveRateModal}
        onAction={removeProposalRate}
        modalType={ModalType.DELETE}
      />
      <ActionModal
        show={showRemoveFileModal}
        title="Delete uploaded file"
        subtitle={
          <span>
            You&apos;re about to delete
            <b className={classes.currentName}>{convertedRemovedFileInfo.linkName || ''} </b> file. Easily upload a new
            one using the Upload files button.
          </span>
        }
        actionButtonText="Delete file"
        closeButtonText="Close"
        onClose={closeRemoveFileModal}
        onAction={removeProductCustomLink}
        modalType={ModalType.DELETE}
      />
      <AddCustomLinkModal onUpdate={updateFileUploadProposal} />
    </>
  );
});

export default RFQProposalPlans;
